import { FileText, ShieldCheck } from "lucide-react";
import { RichText } from "@/components/glossary/Glossary";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const KIND = { AGREEMENT: "Sözleşme", NOTICE: "Bilgilendirme", CONSENT: "İsteğe bağlı izin" };
export const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "medium" }) : "-");

export function DocViewer({ doc, onClose, onAccept, acceptLabel }) {
  if (!doc) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="legal-doc-viewer">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{KIND[doc.kind] || "Belge"} · Sürüm v{doc.version}</div>
          <DialogTitle className="text-xl" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="legal-doc-viewer-title">{doc.title}</DialogTitle>
          {doc.summary && <DialogDescription>{doc.summary}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 -mr-1 border-y border-slate-100 py-4" data-testid="legal-doc-viewer-body"><RichText text={doc.body} /></div>
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
          <div className="text-[10px] text-slate-400 font-mono break-all max-w-md">SHA-256: {doc.content_hash}</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="legal-doc-viewer-close">Kapat</Button>
            {onAccept && <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="legal-doc-viewer-accept" onClick={() => { onAccept(); onClose(); }}>{acceptLabel}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const acceptText = (d) => (d.kind === "NOTICE" ? "Okudum, bilgilendirildim" : "Okudum, kabul ediyorum");

export function DocCheckRow({ doc, checked, onChange, onOpen, tone = "app" }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`legal-row-${doc.code}`}>
      <input type="checkbox" className="mt-1 w-4 h-4 accent-emerald-700 shrink-0" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={`legal-check-${doc.code}`} id={`lc-${doc.code}`} />
      <div className="flex-1 min-w-0">
        <label htmlFor={`lc-${doc.code}`} className="text-sm text-slate-800 cursor-pointer">
          <button type="button" onClick={onOpen} data-testid={`legal-open-${doc.code}`} className="font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950">{doc.title}</button>
          {" "}metnini {doc.kind === "NOTICE" ? "okudum, bilgilendirildim" : "okudum ve kabul ediyorum"}.{doc.required && <span className="text-red-600"> *</span>}
        </label>
        <div className="text-[11px] text-slate-500 mt-0.5">v{doc.version} · {doc.summary}</div>
      </div>
      <FileText className="w-4 h-4 text-slate-300 shrink-0 mt-1" />
    </div>
  );
}

export function ConsentRow({ doc, value, onChange, onOpen, disabled }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`consent-row-${doc.code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{doc.title}</div>
          <div className="text-xs text-slate-500">{doc.summary}</div>
        </div>
        <div className="flex rounded-full border border-slate-200 p-0.5 shrink-0" role="radiogroup">
          {[[true, "İzin ver"], [false, "Reddet"]].map(([v, l]) => (
            <button key={l} type="button" disabled={disabled} data-testid={`consent-${doc.code}-${v ? "yes" : "no"}`} onClick={() => onChange(v)} aria-pressed={value === v}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${value === v ? (v ? "bg-emerald-700 text-white" : "bg-slate-800 text-white") : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
      </div>
      <button type="button" onClick={onOpen} className="text-xs text-emerald-800 hover:underline" data-testid={`consent-open-${doc.code}`}>Amaç, kapsam ve şartları oku</button>
    </div>
  );
}

export const SectionTitle = ({ icon: I = ShieldCheck, title, text }) => (
  <div className="flex items-start gap-2">
    <I className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
    <div><div className="text-sm font-bold text-slate-900">{title}</div>{text && <div className="text-xs text-slate-500">{text}</div>}</div>
  </div>
);
